import _ from "underscore";
import dateMath from '@elastic/datemath';

import { 
	DYNAMIC_FILTER_TYPE, 
	getFiltersByType,
	reactivateFilters,
	deactivateFilters,
} from "lib/helpers/filterHelpers.js";

import { 
	allQuotesDouble,
} from "lib/helpers/mediaHelpers.js";

import * as Actions from "actions/constants/searchActionTypes.js";

import {
	fetchDynamicFilters,
	fetchQueryExpansion,
	saveUserSearch,
} from "actions/fetch";


export const updateQueryExpansion = (stateId, value, force=false, disableDebounce=false, setActiveOnSuccess=false, onSuccess=()=>{}) => {
	return (dispatch, getState) => {
		const { 
			searchTranslationLanguages, 
			searchTranslationEnabled, 
			searchFullTranslationEnabled,
		} = getState().search[stateId];

		dispatch(fetchQueryExpansion({
			stateId,
			queryString: value, 
			disableDebounce,
			actionId: Actions.SEARCH_SET_EXPANDED_QUERY,
			loadingActionId: Actions.SEARCH_SET_EXPANDED_QUERY_LOADING,
			translationEnabled: force || searchTranslationEnabled,
			fullTranslationEnabled: searchTranslationEnabled && searchFullTranslationEnabled,
			languages: searchTranslationLanguages,
			setActiveOnSuccess,
			onSuccess,
		}));
	}
}

export const resetQueryExpansion = (stateId) => {
	return (dispatch, getState) => {
		dispatch({type: Actions.SEARCH_RESET_EXPANDED_QUERY, stateId});	
	}
}

export const checkResetQueryExpansion = (stateId) => {
	return (dispatch, getState) => {
		const { 
			searchTranslationLanguages, 
			searchTranslationEnabled, 
			searchFullTranslationEnabled,
			expandedQueryLoading,
			expandedQueryError,
			activeSearchObj,
			searchInput,
		} = getState().search[stateId];

		if (!activeSearchObj.query) {
			return;
		}
		else {
			const queryNeedsReset = searchInput !== activeSearchObj.query;
			const expansionDisabled = !searchTranslationEnabled || searchTranslationLanguages.length === 0;
			if (queryNeedsReset) {
				dispatch({type: Actions.SEARCH_RESET_SEARCH_INPUT, stateId});	
				dispatch({type: Actions.SEARCH_RESET_EXPANDED_QUERY, stateId});	
				dispatch({type: Actions.SEARCH_RESET_ACTIVE_EXPANDED_QUERY, stateId});	
			}
			if (!expansionDisabled && (queryNeedsReset || expandedQueryError)) {
				dispatch(fetchQueryExpansion({
					stateId,
					queryString: activeSearchObj ? activeSearchObj.query : "", 
					disableDebounce: true,
					actionId: Actions.SEARCH_SET_EXPANDED_QUERY,
					loadingActionId: Actions.SEARCH_SET_EXPANDED_QUERY_LOADING,
					alreadyLoading: expandedQueryLoading,
					translationEnabled: true,
					fullTranslationEnabled: searchTranslationEnabled && searchFullTranslationEnabled,
					languages: searchTranslationLanguages,
				}));
			}
		}
	}
}

export const setActiveExpandedQuery = (stateId, value) => {
	return (dispatch, getState) => {
		dispatch({type: Actions.SEARCH_SET_ACTIVE_EXPANDED_QUERY, stateId, value});	
	}
}

export const selectSearch = (stateId, searchObj, roundUp=true, roundDown=true) => {
	return (dispatch, getState) => {
		const { activeSearchObj } = getState().search[stateId];

		// Do nothing if the selected search is already selected
		if ((activeSearchObj.id !== searchObj.id)
			|| (activeSearchObj.query !== searchObj.query)) {

			// Store changes to the selected search
			dispatch({
				type: Actions.SEARCH_SET_SEARCH_NAME,
        stateId,
				payload: {
					originalSearchName: searchObj && searchObj.name,
				},
			});
			dispatch({
        type: Actions.SEARCH_SET_SEARCH_INPUT,
        stateId,
        searchObj
      });

			// Set the active search
			dispatch({
        type: Actions.SEARCH_SET_ACTIVE_SEARCH,
        stateId,
        searchObj,
				activeSearch: searchObj.query,
				roundUp,
				roundDown,
      });	
			
			// Get the query expansion for the selected search
			dispatch(updateQueryExpansion(stateId, searchObj.query, false, true, true));
		}
	};
}

// Return the new currentZoom here
export const zoomComplete = (stateId, zoom, roundUp=false) => {
	return (dispatch, getState) => {
		const endOfLastDayZoom = {
			...zoom,
			end: zoom.end + (roundUp ? (24 * 60 * 60 * 1000) : 0),
		}
		dispatch({
			type: Actions.SEARCH_ZOOM_COMPLETE,
      stateId,
			currentZoom: endOfLastDayZoom,
		});
	};
}

export const updateSearchInput = (stateId, value) => {
	return (dispatch, getState) => {
		const { activeSearchObj, activeExpandedQuery } = getState().search[stateId];
		if (activeSearchObj.query === value && activeExpandedQuery) {
			dispatch({ type: Actions.SEARCH_SET_EXPANDED_QUERY, stateId, value: activeExpandedQuery});
		}
		else {
			dispatch(resetQueryExpansion(stateId));
		}
		dispatch({
      type: Actions.SEARCH_UPDATE_SEARCH_INPUT,
      stateId,
      value: allQuotesDouble(value)
    });
	}
}

export const updateSearchTimeEnd = (stateId, displayValue, value, initializing=false, roundUp=true) => {
	return { 
    type: Actions.SEARCH_UPDATE_TIME_END,
    stateId,
    displayValue,
    value,
    initializing,
		roundUp,
  };
}

export const updateSearchTimeStart = (stateId, displayValue, value, initializing=false, roundDown=true) => {
	return { 
    type: Actions.SEARCH_UPDATE_TIME_START,
    stateId,
    displayValue,
    value,
    initializing,
		roundDown,
  };
}

// --------- Modals --------
export const closeDeleteSearchModal = (stateId) => {
	return {
		type: Actions.SEARCH_CLOSE_DELETE_SEARCH_MODAL,
    stateId,
	};
};

export const openDeleteSearchModal = (params) => {
	if (params && params.userId) {
		return {
			type: Actions.SEARCH_OPEN_DELETE_SEARCH_MODAL,
      stateId: params.stateId,
			deleteSearchModalData: params,
		};
	} else {
		return () => {};
	}
};

export const closeCreateSearchModal = (stateId) => {
	return {
		type: Actions.SEARCH_CLOSE_CREATE_SEARCH_MODAL,
    stateId,
	};
};

export const openEditSearchModal = (params) => {
	const { 
		stateId, 
		userId, 
		locationStr, 
		name, 
		query, 
		id, 
		additionalAttributes, 
		disableFutureDates, 
		disableTimeControls,
		roundUp,
		roundDown,
	} = params;
	if (userId) {
		return (dispatch) => {
			dispatch({
				type: Actions.SEARCH_OPEN_CREATE_SEARCH_MODAL,
        stateId,
				createSearchCheckmark: false,
				createSearchQuery: query,
				createSearchName: name,
				createSearchTimeStart: additionalAttributes?.timeStart, 
				createSearchTimeEnd: additionalAttributes?.timeEnd, 
				createSearchLocation: locationStr || "",
				createSearchRoundDown: roundDown,
				createSearchRoundUp: roundUp,
				createSearchTranslate: additionalAttributes.translate,
				createSearchTranslateFull: additionalAttributes.translateFull,
				createSearchTranslateLangs: additionalAttributes.translateLangs,
				editingUserSearch: id,
				editingDefaultSearch: additionalAttributes.isDefault,
				disableFutureDates,
				disableTimeControls,
			});
		};
	} else {
		return () => {};
	}
};

export const openCloneSearchModal = (params) => {
	const { 
		stateId, 
		locationStr, 
		query, 
		id, 
		additionalAttributes, 
		disableFutureDates, 
		disableTimeControls,
		roundDown,
		roundUp,
	} = params;
	return (dispatch) => {
		dispatch({
			type: Actions.SEARCH_OPEN_CREATE_SEARCH_MODAL,
      stateId,
			createSearchCheckmark: false,
			createSearchQuery: query,
			createSearchName: "",
			createSearchTimeStart: additionalAttributes.timeStart, 
			createSearchTimeEnd: additionalAttributes.timeEnd, 
			createSearchLocation: locationStr || "",
			createSearchRoundDown: roundDown,
			createSearchRoundUp: roundUp,
			createSearchTranslate: additionalAttributes.translate,
			createSearchTranslateFull: additionalAttributes.translateFull,
			createSearchTranslateLangs: additionalAttributes.translateLangs,
			cloningUserSearch: id,
			disableFutureDates,
			disableTimeControls,
		});
	};
};

export const openCreateSearchModal = (params) => {
	const {
		stateId, 
		disableFutureDates, 
		disableTimeControls,
		roundDown,
		roundUp,
	} = params;
	return (dispatch, getState) => {
		const { 
			searchInput, 
			displayTimeStart, 
			displayTimeEnd, 
			searchTranslationEnabled, 
			searchFullTranslationEnabled, 
			searchTranslationLanguages 
		} = getState().search[stateId];
		const { defaultLocation } = getState().projectConfig;
		dispatch({
			type: Actions.SEARCH_OPEN_CREATE_SEARCH_MODAL,
      stateId,
			createSearchCheckmark: false,
			createSearchQuery: searchInput,
			createSearchName: "",
			createSearchTimeStart: displayTimeStart, 
			createSearchTimeEnd: displayTimeEnd, 
			createSearchLocation: defaultLocation || "",
			createSearchRoundDown: roundDown,
			createSearchRoundUp: roundUp,
			createSearchTranslate: searchTranslationEnabled,
			createSearchTranslateFull: searchFullTranslationEnabled,
			createSearchTranslateLangs: searchTranslationLanguages,
			editingUserSearch: "",
			disableFutureDates,
			disableTimeControls,
		});
	};
};

export const setDefaultSearch = (params) => {
	return (dispatch, getState) => {
		const { 
      stateId, 
      locationStr, 
      name, 
      query, 
      id, 
      searchType, 
      additionalAttributes,
			roundUp,
			roundDown,
    } = params;
		const { searches } = getState().search[stateId];
		// Remove any default flag from other searches if the  
		// requested search is being made the new default
		if (!additionalAttributes.isDefault) {
			const existingDefSearch = searches.find(x => {
				return x.searchType === searchType 
					&& x.additionalAttributes?.isDefault === true
					&& x.id !== params.id;
			});
			if (existingDefSearch) {
				dispatch(saveUserSearch({
          stateId,
					cloningUserSearch: false,
					createSearchLocation: existingDefSearch.locationStr,
					createSearchId: existingDefSearch.id,
					createSearchName: existingDefSearch.name,
					createSearchQuery: existingDefSearch.query,
					createSearchTimeStart: existingDefSearch.additionalAttributes.timeStart,
					createSearchTimeEnd: existingDefSearch.additionalAttributes.timeEnd,
					translate: existingDefSearch.additionalAttributes.translate,
					translateFull: existingDefSearch.additionalAttributes.translateFull,
					translateLangs: existingDefSearch.additionalAttributes.translateLangs,
					editingUserSearch: existingDefSearch.id,
					isDefault: false,
					searchType,
					roundUp,
					roundDown,
				}));
			}
		}
		// Add/Remove the default flag to/from the requested search
		dispatch(saveUserSearch({
      stateId,
			cloningUserSearch: false,
			createSearchLocation: locationStr,
			createSearchId: id,
			createSearchName: name,
			createSearchQuery: query,
			createSearchTimeStart: additionalAttributes?.timeStart || 'now-6M/d',
			createSearchTimeEnd: additionalAttributes?.timeEnd || 'now',
			translate: additionalAttributes.translate,
			translateFull: additionalAttributes.translateFull,
			translateLangs: additionalAttributes.translateLangs,
			editingUserSearch: id,
			isDefault: !additionalAttributes?.isDefault,
			searchType,
			roundUp,
			roundDown,
		}));
	}
}

export const setSavedUserSearches = (stateId, searches) => {
	return (dispatch, getState) => {
		const {api} = getState().login;
		return dispatch({
			type: Actions.SEARCH_RECEIVE_USER_SEARCHES,
			stateId,
			key: api.key,
			searches,
		});
	}
}

export const updateCreateSearchName = (value) => {
	return {
		type: Actions.SEARCH_UPDATE_CREATE_SEARCH_NAME,
		createSearchName: value,
	};
}

export const updateCreateSearchQuery = (value) => {
	return {
		type: Actions.SEARCH_UPDATE_CREATE_SEARCH_QUERY,
		createSearchQuery: allQuotesDouble(value),
	};
}

export const updateCreateSearchTime = (start, end, roundUp=true, roundDown=true) => {
	return { 
    type: Actions.SEARCH_UPDATE_CREATE_SEARCH_TIME,
    start, 
    end,
		roundUp,
		roundDown,
  };
}

export function updateCreateSearchLocation(value) {
	return {
		type: Actions.SEARCH_UPDATE_CREATE_SEARCH_LOCATION,
		createSearchLocation: value,
	};
}

export const updateCreateSearchLanguage = (language) => {
	return {
		type: SEARCH_UPDATE_CREATE_SEARCH_LANGUAGE,
		createSearchLanguage: language,
	};
};

export const setFilterSidebarOpen = (stateId, open) => {
	return { 
    type: Actions.SEARCH_SET_FILTER_SIDEBAR_OPEN,
    stateId,
    open
  };
}

export const setSearchSidebarOpen = (stateId, open) => {
	return { 
    type: Actions.SEARCH_SET_SEARCH_SIDEBAR_OPEN,
    stateId,
    open
  };
}

export const resetActiveSearch = (stateId) => {
	return (dispatch) => {
		dispatch({
      type: Actions.SEARCH_RESET_ACTIVE_SEARCH,
      stateId,
    });
	}
}

export const setActiveSearch = (stateId, searchObj, activeSearch, roundUp=true, roundDown=true) => {
	return { 
    type: Actions.SEARCH_SET_ACTIVE_SEARCH,
    stateId,
    searchObj,
    activeSearch,
		roundUp,
		roundDown,
  };
}

export const filterClick = (stateId, filter) => {
	return { 
    type: Actions.SEARCH_FILTER_CLICK,
    stateId,
    filter,
  };
}


export const receiveFilters = (stateId, filters) => {
	return (dispatch) => {
		dispatch({
			type: Actions.SEARCH_RECEIVE_FILTERS,
			stateId,
			filters,
		});
	}
}

export const fetchingDynamicFilters = (stateId, filter) => {
	return (dispatch) => {
		dispatch({
			type: Actions.SEARCH_FETCHING_DYNAMIC_FILTERS,
			stateId,
			parentFilter: filter,
		});
	}
}

export const receiveDynamicFilters = (stateId, results, filter) => {
	return (dispatch) => {
		dispatch({
			type: Actions.SEARCH_RECEIVE_DYNAMIC_FILTERS,
			stateId,
			newChildren: results,
			parentFilter: filter,
		});
	}
}

export const showMoreFilters = (stateId, filter) => {
	return { 
    type: Actions.SEARCH_SHOW_MORE_FILTERS,
    stateId,
    filter,
  };
}

export const applyFilters = (stateId) => {
	return (dispatch) => {
		dispatch({
      type: Actions.SEARCH_FILTERS_APPLY,
      stateId,
    });
	}
}

export const clearFilters = (stateId, datasetId) => {
	return (dispatch) => {
		dispatch({
			type: Actions.SEARCH_FILTERS_CLEAR,
			stateId,
			datasetId,
		});
	}
}

export const addFiltersImmediate = (stateId, filters) => {
	return (dispatch) => {
		dispatch({
      type: Actions.SEARCH_ADD_FILTERS_IMMEDIATE,
      stateId,
      filters
    });
	}
}

export const setCurrentEntityType = (stateId, entityType) => {
	return (dispatch) => {
		dispatch({
      type: Actions.SEARCH_SET_ENTITY_TYPE,
      stateId,
      entityType
    });
	}
}

export const removeFiltersImmediate = (stateId, filters) => {
	return (dispatch) => {
		dispatch({
      type: Actions.SEARCH_REMOVE_FILTERS_IMMEDIATE,
      stateId,
      filters
    });
	}
}

export const openCloseFilterList = (stateId, filter, open) => {
	return (dispatch) => {
		// Update the open status of the filter list
		dispatch({ 
      type: Actions.SEARCH_SET_FILTER_LIST_OPEN,
      stateId,
      filter,
      open
    });
		// Check if we need to request filters for the list
		if (open) {
			const filterIsDynamic = filter.type === DYNAMIC_FILTER_TYPE;
			// const haveExistingChildren = Array.isArray(filter.children) && (filter.children.length > 0);
			// if (!haveExistingChildren && filterIsDynamic) {
			if (filterIsDynamic) {
				filter.status = "open";
				dispatch(fetchDynamicFilters(stateId, filter)); 
			}
		}
	};
}

export const updateDynamicFilters = (stateId) => {
	return (dispatch, getState) => {
		const {	filters } = getState().search[stateId];
		const filtersToUpdate = getFiltersByType(filters, DYNAMIC_FILTER_TYPE);
		for (let i=0; i<filtersToUpdate.length; i++) {
			dispatch(fetchDynamicFilters(stateId, filtersToUpdate[i])); 
		}
	};
}

export const setFilterFilterString = (stateId, value, filterId) => {
	return (dispatch) => {
		dispatch({ 
      type: Actions.SEARCH_SET_FILTER_FILTER_STRING,
      stateId,
      value,
      filterId,
    });
		dispatch(updateDynamicFilters(stateId));
	};
}

export const setPendingFilterFilterString = (stateId, value, filterId) => {
	return (dispatch) => {
		dispatch({ 
      type: Actions.SEARCH_SET_PENDING_FILTER_FILTER_STRING,
      stateId,
      value,
      filterId,
    });
	};
}

export const selectSavedFilter = (stateId, filter) => {
	return (dispatch, getState) => {
		const {filters } = getState().search[stateId];
		const updatedFilters = reactivateFilters(deactivateFilters(filters), filter.filters);
		dispatch({
      type: Actions.SEARCH_UPDATE_FILTERS, 
      stateId,
      filters: updatedFilters
    });
	};
}

export const setSavedFiltersOpen = (stateId, open) => {
	return (dispatch) => {
		dispatch({ 
      type: Actions.SEARCH_SET_SAVED_FILTERS_OPEN,
      stateId,
      open,
    });
	};
}

export const setShowExpandedQuery = (stateId, show) => {
	return (dispatch) => {
		dispatch({
      type: Actions.SEARCH_SET_SHOW_EXPANDED_QUERY,
      stateId,
      show,
    });
	};
}

export const setSearchTranslationEnabled = (stateId, state) => {
	return (dispatch, getState) => {
		const { searchInput } = getState().search[stateId];
		dispatch({
      type: Actions.SEARCH_SET_TRANSLATION_ENABLED,
      stateId,
      state,
    });
		dispatch(resetQueryExpansion(stateId));
	}
}

export const setSearchFullTranslationEnabled = (stateId, state) => {
	return (dispatch, getState) => {
		const { searchInput } = getState().search[stateId];
		dispatch({
      type: Actions.SEARCH_SET_FULL_TRANSLATION_ENABLED,
      stateId,
      state,
    });
		dispatch(resetQueryExpansion(stateId));
	}
}

export const setSearchTranslationLanguages = (stateId, languages) => {
	return (dispatch, getState) => {
		const { searchInput, expandedQueryLoading } = getState().search[stateId];
		dispatch({
      type: Actions.SEARCH_SET_TRANSLATION_LANGUAGES,
      stateId,
      languages,
    });
		dispatch(resetQueryExpansion(stateId));
	}
}

export const activateSearchTranslationOptions = (stateId) => {
	return { 
    type: Actions.SEARCH_ACTIVATE_TRANSLATION_OPTIONS, 
    stateId,
  };
}

export const hydrateSearchState = (params) => {
	return { 
    type: Actions.SEARCH_HYDRATE_STATE, 
    ...params,
  };
}

export const cancelAllSearchChanges = (stateId) => {
	return { 
    type: Actions.SEARCH_CANCEL_ALL_CHANGES, 
    stateId,
  };
}

export const setSearchQueryObfuscation = (stateId, obfuscate) => {
	return { 
    type: Actions.SEARCH_SET_QUERY_OBFUSCATION, 
    stateId,
    obfuscate,
  };
}

export const setQueryBuilderSearchInput = (stateId, text) => {	
	return { 
    type: Actions.SEARCH_SET_QUERY_BUILDER_SEARCH_INPUT, 
    stateId,
    text,
  };
}

export const openQueryBuilder = (stateId) => {	
	return { 
    type: Actions.OPEN_QUERY_BUILDER, 
    stateId,
  };
}
